/**
 * SimpleLighting — the Lighting window's simple panel: the room's lighting
 * laid out from a few choices.
 *
 * Pick a fixture, how closely they are spaced, how bright and how warm, and
 * the room is filled with an even, symmetric array of them — as many as the
 * room's size and proportions call for (lampLayout.arrayGrid), re-fitted
 * whenever the room changes. A plan of the room shows the array live.
 *
 * It writes the same settings the Advanced panel does (interiorLayout 'array'
 * and friends), so the two never disagree: anything placed by hand in Advanced
 * shows here as "placed by hand", and Generate lays it out again.
 */

import React from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import { DEFAULT_RENDER_SETTINGS, FixtureStyle, RenderSettings, updateRenderSettings } from '../../ui/room3d/renderSettings';
import { MAX_ARRAY_LAMPS, arrayGrid } from '../../ui/room3d/lampLayout';
import { DESIGNER_STRINGS as S } from '../strings';

const T = S.render.lights;

export interface SimpleLightingProps {
  settings: RenderSettings;
  /** The room shown (feet), for the plan and the count. */
  roomSize?: { length: number; width: number };
}

const FIXTURES: FixtureStyle[] = ['pot', 'dome', 'bar'];

/** Coverage: about one light every so many feet. */
const COVERAGES: { id: keyof typeof T.coverages; spacing: number }[] = [
  { id: 'sparse', spacing: 16 },
  { id: 'balanced', spacing: 11 },
  { id: 'bright', spacing: 8 },
];

/**
 * What Generate sets: the array, centred, hanging and fading as the defaults
 * do — anything moved by hand in Advanced put back.
 */
const GENERATED: Partial<RenderSettings> = {
  interiorLayout: 'array',
  interiorX: DEFAULT_RENDER_SETTINGS.interiorX,
  interiorZ: DEFAULT_RENDER_SETTINGS.interiorZ,
  interiorHeight: DEFAULT_RENDER_SETTINGS.interiorHeight,
  interiorFalloff: DEFAULT_RENDER_SETTINGS.interiorFalloff,
  interiorReach: DEFAULT_RENDER_SETTINGS.interiorReach,
};

const AMBER = '#feb100';

/** A drawing of each fixture, seen from the side, lit when chosen. */
const FixtureGlyph: React.FC<{ style: FixtureStyle; active: boolean }> = ({ style, active }) => {
  const lit = active ? AMBER : '#9d9da8';
  const body = active ? '#fff' : '#8a8a94';
  const W = 44, H = 30;
  const ceiling = <line x1={2} y1={3} x2={W - 2} y2={3} stroke={body} strokeWidth={1.5} strokeLinecap="round" />;
  const beam = (x: number, top: number, spread: number) => (
    <path d={`M ${x - 3} ${top} L ${x - spread} ${H - 1} L ${x + spread} ${H - 1} L ${x + 3} ${top} Z`} fill={lit} opacity={active ? 0.28 : 0.14} />
  );
  switch (style) {
    case 'pot':
      return (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
          {ceiling}
          {beam(W / 2, 5, 15)}
          <rect x={W / 2 - 8} y={3} width={16} height={3} rx={1} fill={body} />
          <rect x={W / 2 - 5} y={4} width={10} height={2} fill={lit} />
        </svg>
      );
    case 'dome':
      return (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
          {ceiling}
          {beam(W / 2, 13, 15)}
          <rect x={W / 2 - 9} y={3} width={18} height={2.5} rx={1} fill={body} />
          <path d={`M ${W / 2 - 8} 5.5 A 8 8 0 0 0 ${W / 2 + 8} 5.5 Z`} fill={lit} />
        </svg>
      );
    default:
      return (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
          {ceiling}
          <line x1={W / 2 - 12} y1={3} x2={W / 2 - 12} y2={12} stroke={body} strokeWidth={1} strokeDasharray="1.5 1.5" />
          <line x1={W / 2 + 12} y1={3} x2={W / 2 + 12} y2={12} stroke={body} strokeWidth={1} strokeDasharray="1.5 1.5" />
          {beam(W / 2, 15, 17)}
          <rect x={W / 2 - 16} y={12} width={32} height={4} rx={1.5} fill={body} />
          <rect x={W / 2 - 15} y={14} width={30} height={2} fill={lit} />
        </svg>
      );
  }
};

/** The room from above with its lights where the array puts them. */
const Plan: React.FC<{ length: number; width: number; spacing: number; style: FixtureStyle }> = ({ length, width, spacing, style }) => {
  const grid = arrayGrid(spacing, length, width);
  const scale = Math.min(250 / length, 140 / width);
  const w = length * scale, h = width * scale;
  const glowR = Math.max(10, Math.min(w, h) / 6);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={T.count(grid.cols * grid.rows, T.fixtures[style], grid.cols, grid.rows)}>
      <defs>
        <radialGradient id="sl-glow">
          <stop offset="0%" stopColor={AMBER} stopOpacity={0.55} />
          <stop offset="100%" stopColor={AMBER} stopOpacity={0} />
        </radialGradient>
      </defs>
      <rect x={0.5} y={0.5} width={w - 1} height={h - 1} rx={3} fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.35)" strokeWidth={1} />
      {grid.zs.map((z) => grid.xs.map((x) => {
        const cx = x * scale, cy = z * scale;
        return (
          <g key={`${x}-${z}`}>
            <circle cx={cx} cy={cy} r={glowR} fill="url(#sl-glow)" />
            {style === 'bar'
              ? <rect x={cx - Math.max(6, 2 * scale)} y={cy - 1.5} width={Math.max(12, 4 * scale)} height={3} rx={1.5} fill="#fff" />
              : <circle cx={cx} cy={cy} r={style === 'dome' ? 3.2 : 2.4} fill="#fff" />}
          </g>
        );
      }))}
    </svg>
  );
};

const warmthName = (k: number): string => (k < 4000 ? T.warmthNames.warm : k < 5600 ? T.warmthNames.soft : k <= 6500 ? T.warmthNames.white : T.warmthNames.cool);
const feet = (n: number) => Math.round(n * 10) / 10;

export const SimpleLighting: React.FC<SimpleLightingProps> = ({ settings, roomSize }) => {
  const byHand = settings.interiorLayout !== 'array';
  const style = settings.interiorFixtureStyle;
  const coverage = COVERAGES.reduce((best, c) => (Math.abs(c.spacing - settings.interiorSpacing) < Math.abs(best.spacing - settings.interiorSpacing) ? c : best), COVERAGES[0]);
  const grid = roomSize ? arrayGrid(settings.interiorSpacing, roomSize.length, roomSize.width) : null;

  // Every choice here is a choice about the array: making one makes the layout the array.
  const choose = (patch: Partial<RenderSettings>) => updateRenderSettings({ interiorLayout: 'array', ...patch });

  return (
    <div className="sl">
      {/* The fixture */}
      <div className="sl-section">
        <div className="sl-head"><span>{T.fixture}</span></div>
        <div className="sl-tiles" role="radiogroup" aria-label={T.fixture}>
          {FIXTURES.map((f) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={style === f}
              title={T.fixtureHints[f]}
              className={`sl-tile${style === f ? ' is-active' : ''}`}
              onClick={() => choose({ interiorFixtureStyle: f })}
            >
              <FixtureGlyph style={f} active={style === f} />
              <span className="sl-tile-label">{T.fixtures[f]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* How many: the plan, and the spacing */}
      <div className="sl-section">
        <div className="sl-head">
          <span title={T.coverageHint}>{T.coverage}</span>
          {grid && <span className="sl-head-value">{grid.cols * grid.rows} × {T.fixtures[style]}</span>}
        </div>
        <div className="sl-seg" role="radiogroup" aria-label={T.coverage}>
          {COVERAGES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={coverage.id === c.id && settings.interiorSpacing === c.spacing}
              className={`sl-seg-btn${coverage.id === c.id ? ' is-active' : ''}`}
              onClick={() => choose({ interiorSpacing: c.spacing })}
            >
              {T.coverages[c.id]}
            </button>
          ))}
        </div>
        {roomSize && grid && (
          <div style={{ marginTop: 10 }}>
            <div className={`sl-plan${byHand ? ' is-stale' : ''}`}>
              <Plan length={roomSize.length} width={roomSize.width} spacing={settings.interiorSpacing} style={style} />
            </div>
            <p className="sl-plan-caption">{T.count(grid.cols * grid.rows, T.fixtures[style], grid.cols, grid.rows)}</p>
            <p className="sl-plan-sub">{T.roomSize(feet(roomSize.length), feet(roomSize.width))} · {T.max(MAX_ARRAY_LAMPS)}</p>
          </div>
        )}
      </div>

      {/* How bright, how warm */}
      <div className="sl-section">
        <div className="sl-head">
          <span>{T.brightness}</span>
          <span className="sl-head-value">{Math.round(settings.interiorIntensity * 100)}%</span>
        </div>
        <input
          className="rc-range"
          type="range"
          aria-label={T.brightness}
          min={0}
          max={1.5}
          step={0.01}
          value={settings.interiorIntensity}
          onChange={(e) => updateRenderSettings({ interiorIntensity: Number(e.target.value) })}
        />
        <div className="sl-head" style={{ marginTop: 12 }}>
          <span title={T.ambientHint}>{T.ambient}</span>
          <span className="sl-head-value">{Math.round(settings.ambientIntensity * 100)}%</span>
        </div>
        <input
          className="rc-range"
          type="range"
          aria-label={T.ambient}
          min={0}
          max={1.5}
          step={0.01}
          value={settings.ambientIntensity}
          onChange={(e) => updateRenderSettings({ ambientIntensity: Number(e.target.value) })}
        />
        <div className="sl-head" style={{ marginTop: 12 }}>
          <span>{T.warmth}</span>
          <span className="sl-head-value">{warmthName(settings.interiorWarmth)} · {settings.interiorWarmth} K</span>
        </div>
        <input
          className="sl-warmth"
          type="range"
          aria-label={T.warmth}
          min={2700}
          max={7500}
          step={50}
          value={settings.interiorWarmth}
          onChange={(e) => updateRenderSettings({ interiorWarmth: Number(e.target.value) })}
        />
      </div>

      <div className="sl-section">
        <div className="rc-row">
          <div className="rc-row-head">
            <span className="rc-label">{T.showFixtures}</span>
            <input className="rc-switch" type="checkbox" aria-label={T.showFixtures} checked={settings.interiorFixtures} onChange={(e) => updateRenderSettings({ interiorFixtures: e.target.checked })} />
          </div>
        </div>
        {byHand && <p className="rc-note is-bad" style={{ marginTop: 4 }}>{T.byHand}</p>}
        <button
          type="button"
          className={`sl-generate${byHand ? '' : ' is-quiet'}`}
          title={T.regenerateHint}
          onClick={() => updateRenderSettings(GENERATED)}
        >
          {byHand ? <Sparkles size={15} /> : <RefreshCw size={14} />}
          {byHand ? T.generate : T.regenerate}
        </button>
        <p className="rc-note">{T.planNote}</p>
      </div>
    </div>
  );
};

export default SimpleLighting;
